import React, {RefObject} from "react";
import {connect} from "react-redux";
import {
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
    List,
    ListRowProps,
    ScrollParams,
    Size,
} from "react-virtualized";
import {Nullable} from "@data-force/app";
import {
    getTranslate,
    TranslateFunction,
} from "react-localize-redux";
import classNames from "classnames";
import StoreState from "../../interfaces/store/storeState";
import JobItem from "../JobItem/JobItem";
import {Cursor} from "../../interfaces/store/cursor";
import {
    JobEntity,
    JobRequestParams,
    PhaseConfigNormalized,
} from "../../interfaces/store/actions/payload/job";
import {
    clearJobListData,
    getJobList,
    openJobEditor,
    setJobSearch,
    setSelectedJob,
    showJobDrawer,
} from "../../store/actions/job";
import Spinner from "../common/Spinner/Spinner";
import {
    ClearJobListDataAction,
    GetJobListAction,
    OpenJobEditorAction,
    SetJobSearchAction,
    SetSelectedJobAction,
    ShowJobDrawerAction,
} from "../../interfaces/store/actions/job";
import "./JobList.scss";
import SearchField from "../common/SearchField/SearchField";
import {CLICK_EVENT, DrawerTabs, ENTER_KEY} from "../../utils/constants";
import JobEmpty from "../JobEmpty/JobEmpty";
import {SetFocusedElementAction} from "../../interfaces/store/actions/focusElement";
import {setFocusedElement} from "../../store/actions/focusElement";
import {Editors} from "../../interfaces/common";
import {getOpeningUrl} from "../../utils/common";

interface OwnProps {
    isMobile: boolean
}

interface StateProps {
    translate: TranslateFunction
    cursor: Nullable<Cursor>
    isLoading: boolean
    list: JobEntity[]
    searchValue: Nullable<string>
    filter: string
    activeJobType: string
    selectedJob: Nullable<JobEntity>
    focusedElement: Nullable<RefObject<HTMLElement>>
    openEditor: boolean
    editors: Editors
    phaseConfigList: PhaseConfigNormalized
}

interface DispatchProps {

    getJobList(payload?: JobRequestParams): GetJobListAction

    clearJobListData() : ClearJobListDataAction

    setJobSearch(payload: Nullable<string>): SetJobSearchAction

    setSelectedJob(payload: Nullable<JobEntity>): SetSelectedJobAction

    setFocusedElement(payload: Nullable<RefObject<HTMLElement>>): SetFocusedElementAction

    showJobDrawer(payload: string): ShowJobDrawerAction

    openJobEditor(payload: boolean): OpenJobEditorAction
}

type Props = StateProps & DispatchProps & OwnProps;

class JobList extends React.Component<Props> {
    private static readonly translationPath: string = "components.JobList.JobList";

    private static readonly cache = new CellMeasurerCache({
        defaultHeight: 220,
        fixedHeight: true,
        minWidth: 1000,
        keyMapper: () => 1,
    });

    private static mobileRowHeight: number = 270;

    private jobContainerRefs: RefObject<HTMLDivElement>[] = [];

    constructor(props: Props) {
        super(props);

        this.onScrollHandler = this.onScrollHandler.bind(this);

        this.getAdjustedRowHeight = this.getAdjustedRowHeight.bind(this);

        this.searchJobs = this.searchJobs.bind(this);

        this.noContentRenderer = this.noContentRenderer.bind(this);
    }

    componentDidMount(): void {
        const {
            getJobList: getJobListHandler,
            filter,
        }: Props = this.props;
        getJobListHandler({filter});
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (prevProps.list !== this.props.list) {
            this.jobContainerRefs = this.props.list.map(() => React.createRef());
        }

        if (this.props.selectedJob && !!this.jobContainerRefs.length) {
            this.jobContainerRefs.forEach((ref:RefObject<HTMLDivElement>): void => {
                if (ref.current) {
                    const classes: DOMTokenList = ref.current.classList;
                    if (classes.contains("df-job-selected")) {
                        this.props.setFocusedElement(ref);
                    }
                }
            });
        }

        if (this.props.openEditor) {
            const {
                editors, phaseConfigList, selectedJob,
            }: Props = this.props;

            if (selectedJob) {
                const url: string = getOpeningUrl(editors, phaseConfigList, selectedJob);
                window.open(url);
                this.props.openJobEditor(false);
            }
        }
    }

    componentWillUnmount(): void {
        this.props.clearJobListData();
    }

    /**
     * Scroll handler will load more projects to list
     * @param {number} scrollHeight
     * @param {number} clientHeight
     * @param {number} scrollTop
     */
    onScrollHandler({scrollHeight, clientHeight, scrollTop}: ScrollParams) {
        if ((Math.floor(scrollTop) === (scrollHeight - clientHeight)) || (Math.ceil(scrollTop) === (scrollHeight - clientHeight))) {
            const {
                getJobList: getJobListHandler,
                cursor,
                searchValue,
                filter,
            }: Props = this.props;

            getJobListHandler({
                cursor,
                filter,
                title: searchValue,
                updateList: true,
            });
        }
    }

    /**
     * When element is in focus, calls action which set in store that element as focused element
     */
    onFocusChange(index: number): void {
        this.props.setFocusedElement(this.jobContainerRefs[index]);
    }

    /**
     * Depending on current row width we adjust row height
     * @param {boolean} isMobile
     * @return {number}
     */
    getAdjustedRowHeight(isMobile: boolean): number {
        return isMobile ? JobList.mobileRowHeight : JobList.cache.defaultHeight;
    }

    /**
     * Sets search job field value and triggers search
     * @param {string} value
     */
    searchJobs(value: string): void {
        const {getJobList: getJobListHandler, filter}: Props = this.props;
        getJobListHandler({
            title: value,
            filter,
        });
        this.props.setJobSearch(value);
    }

    /**
     * Open job drawer on current job item on click or enter
     * @param {JobEntity} job
     * @param {React.MouseEvent | React.KeyboardEvent} event
     */
    openJobDrawer(job: JobEntity, event: React.MouseEvent | React.KeyboardEvent): void {
        const {key, type, currentTarget} = event as React.KeyboardEvent;

        if (currentTarget === document.activeElement && (type === CLICK_EVENT || key === ENTER_KEY)) {
            this.props.setSelectedJob(job);
            this.props.showJobDrawer(DrawerTabs.STATS);
        }
    }

    /**
     * Show placeholder icon with text when there is no data for the list
     * @return JSX.Element
     */
    noContentRenderer(): JSX.Element {
        const {isLoading, activeJobType, searchValue}: Props = this.props;
        const type:string = searchValue?.length ? "NOT_FOUND" : activeJobType;

        return isLoading ? <></> : <JobEmpty jobType={type}/>;
    }

    renderRow({
        key, style, parent, index,
    }: ListRowProps): React.ReactNode {
        const jobData: JobEntity = this.props.list[index];
        const isJobSelected: boolean = this.props.selectedJob?.id === jobData.id;
        const clsNames: string = classNames({
            "df-job-container": true,
            "df-job-selected": isJobSelected,
        });
        return (
            <CellMeasurer
                cache={JobList.cache}
                key={key}
                parent={parent}
            >
                <div style={{...style, display: "flex"}} key={key}>
                    <div
                        className={clsNames}
                        role="button"
                        tabIndex={0}
                        onClick={this.openJobDrawer.bind(this, jobData)}
                        onKeyDown={this.openJobDrawer.bind(this, jobData)}
                        onFocus={this.onFocusChange.bind(this, index)}
                        ref={this.jobContainerRefs[index]}
                    >
                        <JobItem data={jobData} hideBtnDropdown={false}/>
                    </div>
                </div>
            </CellMeasurer>
        );
    }

    render() {
        const {
            isLoading, list, isMobile, translate,
        }: Props = this.props;

        return (
            <>
                {isLoading && (<Spinner size="large"/>)}
                <div className="df-job-list-search">
                    <SearchField
                        onSearch={this.searchJobs}
                        inputProps={{
                            placeholder: translate(`${JobList.translationPath}.searchJobs.text`) as string,
                            "aria-label": translate(`${JobList.translationPath}.searchJobs.text`) as string,
                        }}
                    />
                </div>
                <AutoSizer>
                    {({ width, height }: Size): React.ReactNode => (
                        <List
                            height={height - 40}
                            width={width}
                            rowRenderer={this.renderRow.bind(this)}
                            deferredMeasurementCache={JobList.cache}
                            rowCount={list.length}
                            rowHeight={this.getAdjustedRowHeight(isMobile)}
                            overscanRowCount={10}
                            onScroll={this.onScrollHandler}
                            noRowsRenderer={this.noContentRenderer}
                            aria-label={translate(`${JobList.translationPath}.listOfJobs.text`) as string}
                        />
                    )}
                </AutoSizer>
            </>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        translate: getTranslate(state.localize),
        cursor: state.job.cursor,
        isLoading: state.mask.isLoading,
        list: state.job.list,
        searchValue: state.job.searchValue,
        filter: state.job.filter,
        activeJobType: state.job.activeJobType,
        selectedJob: state.job.selectedJob,
        focusedElement: state.focusElement.focusedElement,
        openEditor: state.job.openEditor,
        phaseConfigList: state.job.phaseConfigList,
        editors: state.serviceProperties.editors,
    };
}

const mapDispatchToProps: DispatchProps = {
    getJobList,
    setJobSearch,
    setSelectedJob,
    clearJobListData,
    showJobDrawer,
    setFocusedElement,
    openJobEditor,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobList);
